import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';
import {
  Modal,
  ModalFooter,
  ModalButton,
  Input,
  Textarea,
  Checkbox,
  FormGrid,
  Select,
} from './ui';
import { PAYMENT_TERMS_OPTIONS } from '../utils/poUtils';

const EMPTY = {
  name: '',
  vendor_code: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  payment_terms: '',
  default_ship_via: '',
  notes: '',
  is_active: true,
};

export default function VendorEditModal({ isOpen, onClose, vendor, onSaved }) {
  const isEdit = Boolean(vendor?.id);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: vendor?.name || '',
      vendor_code: vendor?.vendor_code || '',
      contact_name: vendor?.contact_name || '',
      contact_email: vendor?.contact_email || '',
      contact_phone: vendor?.contact_phone || '',
      address: vendor?.address || '',
      city: vendor?.city || '',
      state: vendor?.state || '',
      zip: vendor?.zip || '',
      payment_terms: vendor?.payment_terms || '',
      default_ship_via: vendor?.default_ship_via || '',
      notes: vendor?.notes || '',
      is_active: vendor?.is_active ?? true,
    });
  }, [isOpen, vendor]);

  const setField = (key) => (e) => {
    const val =
      e?.target?.type === 'checkbox'
        ? e.target.checked
        : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [key]: val }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Vendor name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        vendor_code: form.vendor_code.trim() || null,
      };
      const query = isEdit
        ? supabase.from('suppliers').update(payload).eq('id', vendor.id)
        : supabase.from('suppliers').insert(payload);
      const { data, error } = await query.select().single();
      if (error) throw error;
      toast.success(isEdit ? 'Vendor updated.' : 'Vendor created.');
      onSaved?.(data);
      onClose();
    } catch (err) {
      console.error('Vendor save failed:', err);
      toast.error(err.message || 'Failed to save vendor.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Vendor' : 'New Vendor'}
      size="lg"
    >
      <div className="space-y-4">
        <FormGrid columns={2}>
          <Input label="Name" value={form.name} onChange={setField('name')} required />
          <Input
            label="Vendor Code"
            value={form.vendor_code}
            onChange={setField('vendor_code')}
            placeholder="Optional short ID"
          />
        </FormGrid>

        <FormGrid columns={3}>
          <Input
            label="Contact Name"
            value={form.contact_name}
            onChange={setField('contact_name')}
          />
          <Input
            label="Contact Email"
            type="email"
            value={form.contact_email}
            onChange={setField('contact_email')}
          />
          <Input
            label="Contact Phone"
            value={form.contact_phone}
            onChange={setField('contact_phone')}
          />
        </FormGrid>

        <Input
          label="Address"
          value={form.address}
          onChange={setField('address')}
        />
        <FormGrid columns={3}>
          <Input label="City" value={form.city} onChange={setField('city')} />
          <Input label="State" value={form.state} onChange={setField('state')} />
          <Input label="ZIP" value={form.zip} onChange={setField('zip')} />
        </FormGrid>

        <FormGrid columns={2}>
          <Select
            label="Payment Terms"
            value={form.payment_terms}
            onChange={setField('payment_terms')}
            options={PAYMENT_TERMS_OPTIONS}
            placeholder="— Select —"
          />
          <Input
            label="Default Ship Via"
            value={form.default_ship_via}
            onChange={setField('default_ship_via')}
            placeholder="e.g. UPS Ground"
          />
        </FormGrid>

        <Textarea
          label="Notes"
          value={form.notes}
          onChange={setField('notes')}
          rows={3}
        />

        <Checkbox
          label="Active"
          checked={form.is_active}
          onChange={setField('is_active')}
        />

        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </ModalButton>
          <ModalButton variant="primary" onClick={handleSave} loading={saving}>
            {isEdit ? 'Save Changes' : 'Create Vendor'}
          </ModalButton>
        </ModalFooter>
      </div>
    </Modal>
  );
}
